import { isPlatformBrowser } from '@angular/common';
import { InjectionToken, makeEnvironmentProviders, PLATFORM_ID } from '@angular/core';

/**
 * The result of the lazy Leaflet getter. Its value depends on the platform type:
 *
 * - **Browser:** Leaflet library interface (i.e. type of `L` in `import L from 'leaflet';`).
 * - **Server:** `false`.
 *
 * ### Rationale
 *
 * Leaflet cannot be used on the server (SSR mode) as it immediately tries to use the `window`
 * object.
 */
export type LazyLeaflet = typeof import('leaflet') | false;

/**
 * A function that lazy-loads Leaflet conditionally based on the platform type. It returns a promise
 * that resolves to a different value depending on the platform type:
 *
 * - **Browser:** Leaflet library interface (i.e. type of `L` in `import L from 'leaflet';`).
 * - **Server:** `false`.
 *
 * ### Rationale
 *
 * Leaflet cannot be used on the server (SSR mode) as it immediately tries to use the `window`
 * object.
 */
export type LazyLeafletGetter = () => Promise<LazyLeaflet>;

/**
 * Injection token for a function that lazy-loads Leaflet conditionally based on the platform type.
 * It returns a promise that resolves to a different value depending on the platform type:
 *
 * - **Browser:** Leaflet library interface (i.e. type of `L` in `import L from 'leaflet';`).
 * - **Server:** `false`.
 *
 * ### Rationale
 *
 * Leaflet cannot be used on the server (SSR mode) as it immediately tries to use the `window`
 * object.
 */
export const LAZY_LEAFLET_GETTER = new InjectionToken<LazyLeafletGetter>('Lazy Leaflet getter');

/**
 * Provides a function that lazy-loads Leaflet conditionally based on the platform type. It returns
 * a promise that resolves to a different value depending on the platform type:
 *
 * - **Browser:** Leaflet library interface (i.e. type of `L` in `import L from 'leaflet';`).
 * - **Server:** `false`.
 *
 * ### Rationale
 *
 * Leaflet cannot be used on the server (SSR mode) as it immediately tries to use the `window`
 * object.
 */
export const provideLazyLeafletGetter = () =>
  makeEnvironmentProviders([
    {
      provide: LAZY_LEAFLET_GETTER,
      deps: [PLATFORM_ID],
      useFactory:
        (platformId: Object): LazyLeafletGetter =>
        async () =>
          isPlatformBrowser(platformId) ? (await import('leaflet')).default : false,
    },
  ]);
